import { useEffect, useState } from "react";

import { ComponentStyleMode, ComponentStyleModeProvider, ResolveStyleModeOptions, useResolvedStyleMode } from "./ComponentStyleMode";
import { ComponentTokenModule, loadComponentTokensBatch } from "./loadComponentTokens";

type ClassMap = Record<string, string>;

/** A dynamic import that resolves to a CSS module default export. */
export type LazyClassMap = () => Promise<{ default: ClassMap }>;

export interface CreateComponentStyleHookOptions {
  legacyStyles: ClassMap | LazyClassMap;
  tokenStyles: ClassMap;
  componentTokens?: ComponentTokenModule | ComponentTokenModule[];
  modeOptions?: ResolveStyleModeOptions;
}

export interface ResolvedComponentStyles {
  mode: ComponentStyleMode;
  isLegacy: boolean;
  isToken: boolean;
  styles: ClassMap;
  cx: (...classNames: Array<string | undefined | null | false>) => string;
  getComponentClassName: (...classNames: Array<string | undefined | null | false>) => string;
  StyleModeBoundary: typeof ComponentStyleModeProvider;
}

const joinClasses = (...classNames: Array<string | undefined | null | false>): string => classNames.filter(Boolean).join(" ");

const normalizeComponentTokens = (componentTokens?: ComponentTokenModule | ComponentTokenModule[]): ComponentTokenModule[] => {
  if (!componentTokens) {
    return [];
  }

  const list = Array.isArray(componentTokens) ? componentTokens : [componentTokens];
  return [...new Set(list.filter(Boolean))];
};

const isLazyClassMap = (styles: ClassMap | LazyClassMap): styles is LazyClassMap => typeof styles === "function";

// Module-level cache so a lazy import is only fetched once across all instances.
const lazyCache = new Map<LazyClassMap, ClassMap | null>();

export const createComponentStyleHook = <TProps extends object>({
  legacyStyles,
  tokenStyles,
  componentTokens,
  modeOptions,
}: CreateComponentStyleHookOptions) => {
  const tokenModules = normalizeComponentTokens(componentTokens);
  const isLazy = isLazyClassMap(legacyStyles);

  return (props: TProps): ResolvedComponentStyles => {
    const mode = useResolvedStyleMode(props, modeOptions);

    // For lazy legacy styles: load on demand when mode is "legacy".
    const [loadedLegacy, setLoadedLegacy] = useState<ClassMap | null>(isLazy ? (lazyCache.get(legacyStyles) ?? null) : null);

    useEffect(() => {
      if (mode === "token" && tokenModules.length > 0) {
        void loadComponentTokensBatch(tokenModules);
      }
    }, [mode]);

    useEffect(() => {
      if (mode !== "legacy" || !isLazy) return;

      const cached = lazyCache.get(legacyStyles as LazyClassMap);
      if (cached) {
        setLoadedLegacy(cached);
        return;
      }

      let cancelled = false;
      void (legacyStyles as LazyClassMap)()
        .then((mod) => {
          lazyCache.set(legacyStyles as LazyClassMap, mod.default);
          if (!cancelled) setLoadedLegacy(mod.default);
        })
        .catch((err) => {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[createComponentStyleHook] Failed to load legacy styles:", err);
          }
        });
      return () => {
        cancelled = true;
      };
    }, [mode]);

    let styles: ClassMap;
    if (mode === "legacy") {
      if (isLazy) {
        // Use loaded legacy styles, or fall back to token styles while loading.
        styles = loadedLegacy ?? tokenStyles;
      } else {
        styles = legacyStyles as ClassMap;
      }
    } else {
      styles = tokenStyles;
    }

    return {
      mode,
      isLegacy: mode === "legacy",
      isToken: mode === "token",
      styles,
      cx: joinClasses,
      getComponentClassName: (...classNames) => joinClasses(...classNames),
      StyleModeBoundary: ComponentStyleModeProvider,
    };
  };
};

export interface CreateTokenStyleHookOptions {
  tokenStyles: ClassMap;
  componentTokens?: ComponentTokenModule | ComponentTokenModule[];
}

/**
 * Token-only variant of {@link createComponentStyleHook} for components that
 * have no legacy SCSS support. It always resolves token mode and ignores any
 * parent legacy boundary. Use this instead of passing an empty `legacyStyles`
 * so that legacy styles remain required for components that still rely on them.
 */
export const createTokenStyleHook = <TProps extends object>({
  tokenStyles,
  componentTokens,
}: CreateTokenStyleHookOptions) => {
  const tokenModules = normalizeComponentTokens(componentTokens);

  return (_props: TProps): ResolvedComponentStyles => {
    useEffect(() => {
      if (tokenModules.length > 0) {
        void loadComponentTokensBatch(tokenModules);
      }
    }, [tokenModules]);

    return {
      mode: "token",
      isLegacy: false,
      isToken: true,
      styles: tokenStyles,
      cx: joinClasses,
      getComponentClassName: (...classNames) => joinClasses(...classNames),
      StyleModeBoundary: ComponentStyleModeProvider,
    };
  };
};
